import { Download, Eye, FileImage, FileText, MoreHorizontal, RotateCcw, Trash2, X } from "lucide-react";
import type { ReactNode } from "react";
import type { Attachment } from "../../domain/entities";
import { attachmentKind, attachmentTypeLabel, formatAttachmentBytes, isPreviewableAttachment } from "../../domain/attachments";
import type { DraftAttachment } from "./useAttachmentUploader";

export function AttachmentRow({ attachment, draft, source, onPreview, onDownload, onRemove, onRetry, onCancel, onRemoveDraft, onSourceClick, busy = false }: {
  attachment?: Attachment;
  draft?: DraftAttachment;
  source?: ReactNode;
  onPreview?: (attachment: Attachment) => void;
  onDownload?: (attachment: Attachment) => void;
  onRemove?: (attachment: Attachment) => void;
  onRetry?: (draft: DraftAttachment) => void;
  onCancel?: (draft: DraftAttachment) => void;
  onRemoveDraft?: (draft: DraftAttachment) => void;
  onSourceClick?: () => void;
  busy?: boolean;
}) {
  if (draft) {
    const done = draft.status === "uploaded";
    const cancellable = draft.status === "validating" || draft.status === "waiting" || draft.status === "uploading";
    return <article className={`attachment-row attachment-draft ${draft.status}`} aria-label={`${draft.file.name} ${draft.status}`}>
      <span className="attachment-icon" aria-hidden="true"><FileText size={18} /></span>
      <div className="attachment-main">
        <strong className="attachment-name">{draft.file.name}</strong>
        <span className="attachment-meta">{formatAttachmentBytes(draft.file.size)} · {draft.error ?? statusText(draft)}</span>
        {(draft.status === "uploading" || draft.status === "waiting" || draft.status === "validating") && <progress className="attachment-progress" max={1} value={draft.progress} aria-label={`Uploading ${draft.file.name}`} />}
      </div>
      <div className="attachment-actions">
        {cancellable && <button type="button" className="button small" onClick={() => onCancel?.(draft)}>Cancel</button>}
        {!cancellable && !done && draft.status !== "uploaded" && <button type="button" className="button small" aria-label={`Retry ${draft.file.name}`} onClick={() => onRetry?.(draft)}><RotateCcw size={13} />Retry</button>}
        <button type="button" className="icon-button" aria-label={`Remove ${draft.file.name}`} onClick={() => onRemoveDraft?.(draft)}><X size={15} /></button>
      </div>
    </article>;
  }
  if (!attachment) return null;
  if (attachment.state === "deleted") {
    return <div className="attachment-row attachment-tombstone" role="note" aria-label={`${attachment.originalName} removed`}>
      <span className="attachment-icon" aria-hidden="true"><Trash2 size={17} /></span>
      <div className="attachment-main"><strong className="attachment-name">Attachment removed</strong><span className="attachment-meta">{attachment.originalName} · {formatAttachmentBytes(attachment.byteSize)} · removed {formatTime(attachment.removedAt)}</span></div>
    </div>;
  }
  const previewable = isPreviewableAttachment(attachment);
  const kind = attachmentKind(attachment);
  const Icon = kind === "image" ? FileImage : FileText;
  return <article className="attachment-row" aria-label={attachment.originalName}>
    <span className="attachment-icon" aria-hidden="true"><Icon size={18} /></span>
    <div className="attachment-main">
      {previewable
        ? <button type="button" className="attachment-name attachment-preview-trigger" aria-label={`Preview ${attachment.originalName}`} onClick={() => onPreview?.(attachment)}>{attachment.originalName}</button>
        : <strong className="attachment-name">{attachment.originalName}</strong>}
      <span className="attachment-meta">{attachmentTypeLabel(attachment)} · {formatAttachmentBytes(attachment.byteSize)} · {formatTime(attachment.readyAt ?? attachment.createdAt)}</span>
      {source && <span className="attachment-source">{source}</span>}
    </div>
    <div className="attachment-actions">
      <button type="button" className="button small" aria-label={`Download ${attachment.originalName}`} disabled={busy} onClick={() => onDownload?.(attachment)}><Download size={13} />Download</button>
      <details className="attachment-more">
        <summary className="icon-button" aria-label={`More for ${attachment.originalName}`}><MoreHorizontal size={16} /></summary>
        <div className="attachment-more-list"><button type="button" className="button small danger" aria-label={`Remove ${attachment.originalName}`} disabled={busy} onClick={(event) => { event.currentTarget.closest("details")?.removeAttribute("open"); onRemove?.(attachment); }}><Trash2 size={13} />Remove</button></div>
      </details>
    </div>
  </article>;
}

function statusText(draft: DraftAttachment) {
  if (draft.status === "validating") return "Validating…";
  if (draft.status === "waiting") return "Waiting…";
  if (draft.status === "uploading") return `Uploading ${Math.round(draft.progress * 100)}%`;
  if (draft.status === "uploaded") return "Uploaded";
  if (draft.status === "cancelled") return "Cancelled";
  return "Upload failed";
}

function formatTime(value: string | null) {
  return value ? new Date(value).toLocaleString("en", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Just now";
}
